import { createSelector } from '@ngrx/store';
import { PayloadAction, createSlice } from 'ngrx-slice';
import { EntityState, createSliceEntityAdapter } from 'ngrx-slice/entity';

import {
  SegmentDiscountCreateRequest,
  SegmentDiscountResponse,
} from '@app/api/models';

export const availableMerchantsForSegmentDiscount = [
  'RETAILER_AS_HOTEL',
  'RETAILER_AS_RESTAURANT',
  'RETAILER_AS_CAFE',
  'RETAILER_AS_RETAIL_CHAIN',
  'RETAILER_AS_TRAFIK',
  'RETAILER_AS_OTHER_RETAILER',
];
const availableProductsForSegmentDiscount = [
  'BEER',
  'GRAPE_WINE',
  'CHAMPAGNE',
  'SPIRIT',
  'OTHER',
  'COFFEE',
  'TEA_OR_MEDICINAL_TEA',
  'MINERAL_WATER',
  'SODA_POP',
  'ICE_TEA',
  'ENERGY_DRINK',
  'COCKTAIL_SYRUP',
  'SYRUP',
  'FRUIT_OR_VEGETABLE_JUICE',
];

// function getInitialSegmentDiscounts(): Map<
//   string,
//   SegmentDiscountResponse | null
// > {
//   const discountMap = new Map<string, SegmentDiscountResponse | null>();
//   availableMerchantsForSegmentDiscount.forEach((merchantType) => {
//     availableProductsForSegmentDiscount.forEach((productType) => {
//       discountMap.set(`${merchantType}|${productType}`, null);
//     });
//   });
//   return discountMap;
// }
export function getInitialSegmentDiscounts(): Array<
  SegmentDiscountResponse & { key: string }
> {
  return availableMerchantsForSegmentDiscount.flatMap((merchantType) =>
    availableProductsForSegmentDiscount.map(
      (productType) =>
        ({
          key: `${merchantType}|${productType}`,
          merchantType,
          productType,
          type: 'SGM',
          startDate: '',
          endDate: '',
          discountMode: 'PERCENT',
          discountValue: 0,
          id: '',
        } as SegmentDiscountResponse & { key: string })
    )
  );
}

export interface State {
  // segmentDiscounts: Map<string, SegmentDiscountResponse | null>;
  segmentDiscounts: EntityState<SegmentDiscountResponse & { key: string }>;
  segmentDiscountsLoading: boolean;
  editRowIndex: string;
}

export const segmentDiscountsAdapter = createSliceEntityAdapter<
  SegmentDiscountResponse & { key: string }
>({
  selectId: (a) => a.key ?? '',
});

export const initialState: State = {
  // segmentDiscounts: getInitialSegmentDiscounts(),
  segmentDiscounts: segmentDiscountsAdapter.getInitialState(),
  segmentDiscountsLoading: false,
  editRowIndex: '',
};

export const {
  actions: SegmentDiscountsActions,
  selectors,
  ...SegmentDiscountsFeature
} = createSlice({
  name: 'segment-discount',
  initialState,
  reducers: {
    loadSegmentDiscounts: {
      trigger: (state) => {
        segmentDiscountsAdapter.setAll(
          state.segmentDiscounts,
          getInitialSegmentDiscounts()
        );
        state.segmentDiscountsLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{ response: Array<SegmentDiscountResponse> }>
      ) => {
        segmentDiscountsAdapter.setMany(
          state.segmentDiscounts,
          action.response.map((r) => ({
            ...r,
            key: `${r.merchantType}|${r.productType}`,
          }))
        );
        state.segmentDiscountsLoading = false;
      },
      failure: () => {},
    },
    setEditRowIndex: {
      trigger: (state, action: PayloadAction<{ index: string }>) => {
        state.editRowIndex = action.index;
      },
      success: () => {},
    },
    createSegmentDiscount: {
      trigger: (
        state,
        action: PayloadAction<{ discount: SegmentDiscountCreateRequest }>
      ) => {},
      success: (
        state,
        action: PayloadAction<{ discount: SegmentDiscountResponse }>
      ) => {
        segmentDiscountsAdapter.upsertOne(state.segmentDiscounts, {
          ...action.discount,
          key: `${action.discount.merchantType}|${action.discount.productType}`,
        });
        state.segmentDiscountsLoading = false;
        state.editRowIndex = '';
      },
      failure: () => {},
    },
    updateSegmentDiscount: {
      trigger: (
        state,
        action: PayloadAction<{ discount: SegmentDiscountResponse }>
      ) => {},
      success: (
        state,
        action: PayloadAction<{ discount: SegmentDiscountResponse }>
      ) => {
        segmentDiscountsAdapter.upsertOne(state.segmentDiscounts, {
          ...action.discount,
          key: `${action.discount.merchantType}|${action.discount.productType}`,
        });
        state.segmentDiscountsLoading = false;
        state.editRowIndex = '';
      },
      failure: () => {},
    },
    deleteSegmentDiscount: {
      trigger: (
        state,
        action: PayloadAction<{ discounts: Array<SegmentDiscountResponse> }>
      ) => {},
      success: (
        state,
        action: PayloadAction<{ discounts: Array<SegmentDiscountResponse> }>
      ) => {
        segmentDiscountsAdapter.upsertMany(
          state.segmentDiscounts,
          action.discounts.map(
            (discount) =>
              ({
                key: `${discount.merchantType}|${discount.productType}`,
                merchantType: discount.merchantType,
                productType: discount.productType,
                type: 'SGM',
                startDate: '',
                endDate: '',
                discountMode: 'PERCENT',
                discountValue: 0,
                id: '',
              } as SegmentDiscountResponse & { key: string })
          )
        );
        state.segmentDiscountsLoading = false;
      },
      failure: () => {},
    },
  },
});

export const selectSegmentDiscountsList = createSelector(
  selectors.selectSegmentDiscounts,
  (discounts) => Object.values(discounts.entities)
);

export const SegmentDiscountsSelectors = {
  ...selectors,
  selectSegmentDiscountsList,
  selectSegmentDiscountGroups: createSelector(
    selectSegmentDiscountsList,
    (discounts) =>
      discounts.reduce((group: any, discount) => {
        const merchantType = discount.merchantType ?? '';
        group[merchantType] = group[merchantType] ?? [];
        group[merchantType].push(discount);
        return group;
      }, {})
  ),
};
